import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
    getLeadAccountDailyStats,
    getLeadAccounts
} from '../api';
import { logError } from '../api';
import { isPropietario, isGestion, isEspectador, isAdmin, getCurrentUser } from '../services/AuthService';
import GenericList from './Global/GenericList';
import LeadAccountDailyStatsSearchForm from './LeadAccountDailyStatsSearchForm';
import { useSorting } from '../hooks/useSorting';
import useFilterPersistence from '../hooks/useFilterPersistence';

const LeadAccountDailyStatsList = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const today = new Date().toISOString().split('T')[0];
    const [searchFilters, setSearchFilters] = useFilterPersistence('filters_lead_account_history', {
        statDateFrom: today,
        statDateTo: today
    });
    const [pagination, setPagination] = useFilterPersistence('pagination_lead_account_history', { page: 1, pageSize: 50 });
    const currentPage = pagination.page;
    const itemsPerPage = pagination.pageSize;
    const [leadAccounts, setLeadAccounts] = useState([]);

    useEffect(() => {
        loadLeadAccounts();
    }, []);

    const loadLeadAccounts = async () => {
        try {
            const response = await getLeadAccounts(1, 1000, false);
            setLeadAccounts(response.items || []);
        } catch (error) {
            console.error('Error loading lead accounts:', error);
        }
    };

    const { orderBy, orderDesc, handleSort } = useSorting();
    const navigate = useNavigate();
    const isSuperUser = isAdmin() || isPropietario() || isGestion() || isEspectador();

    const loadStats = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getLeadAccountDailyStats(
                currentPage, 
                itemsPerPage, 
                false, 
                orderBy, 
                orderDesc, 
                searchFilters
            );
            setStats(response.items || []);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            logError(error);
            console.error('Error loading stats:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, orderBy, orderDesc, searchFilters]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);



    const handleSearch = useCallback((filters) => {
        setSearchFilters(filters);
        setPagination({ ...pagination, page: 1 });
    }, [pagination]);

    const columns = [
        { key: 'id', header: 'ID', sortable: true },
        {
            key: 'leadAccount.name',
            header: 'Cuenta de Lead',
            sortable: true,
            render: (stat) => stat.leadAccount?.name || ''
        },
        ...(isSuperUser ? [{
            key: 'assignedLead',
            header: 'Lead Asignado',
            sortable: false,
            render: (stat) => stat.leadAccount?.assignedLead?.username || ''
        }] : []),
        {
            key: 'leadAccount.zipCode',
            header: 'CP',
            sortable: true,
            render: (stat) => stat.leadAccount?.zipCode || ''
        },
        {
            key: 'leadAccount.email',
            header: 'Correo',
            sortable: true,
            render: (stat) => stat.leadAccount?.email || ''
        },
        {
            key: 'leadAccount.leadSource.name',
            header: 'Fuente del Lead',
            sortable: true,
            render: (stat) => stat.leadAccount?.leadSource?.name || ''
        },
        { 
            key: 'statDate', 
            header: 'Fecha', 
            sortable: true,
            render: (stat) => new Date(stat.statDate).toLocaleDateString()
        },
        { key: 'leadCount', header: 'Cantidad de Leads', sortable: true }
    ];

    return (
        <Container fluid className="my-4" data-testid="lead-account-history-container">
            <Row className="mb-3">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h1 data-testid="page-title">Histórico de Cuentas de Lead</h1>
                        <Button 
                            variant="outline-primary" 
                            onClick={() => navigate('/daily-lead-stats')}
                            data-testid="view-daily-stats-button"
                        >
                            Ver Estadísticas Diarias
                        </Button>
                    </div>
                </Col>
            </Row>
            <GenericList
                title=""
                data={stats}
                columns={columns}
                loading={loading}
                totalCount={totalCount}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setPagination({ ...pagination, page })}
                onItemsPerPageChange={(pageSize) => setPagination({ page: 1, pageSize })}
                deleteMessage="¿Estás seguro de que deseas eliminar esta estadística?"
                emptyMessage="No se encontraron estadísticas."
                onSort={handleSort}
                sortable={true}
                containerFluid={true}
                showSearch={true}
                SearchComponent={LeadAccountDailyStatsSearchForm}
                searchProps={{
                    onSearch: handleSearch,
                    leadAccounts: leadAccounts
                }}
            />
        </Container>
    );
};

export default LeadAccountDailyStatsList;